import { useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useNotifications } from '../hooks/useNotifications'

const typeIcons = {
  new_member: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
      <circle cx="12" cy="7" r="4"/>
    </svg>
  ),
  subscription_expired: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10"/>
      <polyline points="12 6 12 12 16 14"/>
    </svg>
  ),
  donation: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
    </svg>
  ),
  fixture: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10"/>
      <path d="M12 8v4l3 3"/>
    </svg>
  ),
  campaign: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
    </svg>
  ),
  general: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
      <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
    </svg>
  ),
}

const typeColors = {
  new_member: 'text-blue-400',
  subscription_expired: 'text-yellow-400',
  donation: 'text-green-400',
  fixture: 'text-purple-400',
  campaign: 'text-orange-400',
  general: 'text-gray-400',
}

const DROPDOWN_WIDTH = 320
const DROPDOWN_MAX_HEIGHT = 400

export default function NotificationBell() {
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications()
  const [open, setOpen] = useState(false)
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 })
  const bellRef = useRef(null)
  const dropdownNodeRef = useRef(null)
  const navigate = useNavigate()

  // Store dropdown DOM node via callback ref so it's always current
  const setDropdownRef = useCallback((node) => {
    dropdownNodeRef.current = node
  }, [])

  useEffect(() => {
    const handleClickOutside = (e) => {
      const clickedBell = bellRef.current && bellRef.current.contains(e.target)
      const clickedDropdown = dropdownNodeRef.current && dropdownNodeRef.current.contains(e.target)
      if (!clickedBell && !clickedDropdown) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleOpen = () => {
    if (!open && bellRef.current) {
      const rect = bellRef.current.getBoundingClientRect()
      setDropdownPos({
        top: Math.max(8, rect.top - DROPDOWN_MAX_HEIGHT - 8),
        left: rect.left,
      })
    }
    setOpen(prev => !prev)
  }

  const handleNotificationClick = async (notification) => {
    await markAsRead(notification.id)
    setOpen(false)
    if (notification.link) navigate(notification.link)
  }

  const handleDismiss = async (e, notification) => {
    e.stopPropagation()
    await markAsRead(notification.id)
  }

  const timeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000)
    if (seconds < 60) return 'just now'
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`
    return `${Math.floor(seconds / 86400)}d ago`
  }

  const dropdown = open && createPortal(
    <div
      ref={setDropdownRef}
      style={{
        position: 'fixed',
        top: dropdownPos.top,
        left: dropdownPos.left,
        zIndex: 9999,
        width: `min(${DROPDOWN_WIDTH}px, calc(100vw - 16px))`,
      }}
      className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-white/10 shadow-2xl"
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 dark:border-white/10 flex justify-between items-center">
        <span className="text-xs font-bold uppercase tracking-widest text-gray-900 dark:text-white">
          Notifications {unreadCount > 0 && (
            <span className="ml-1 text-green-600 dark:text-green-400">({unreadCount})</span>
          )}
        </span>
        {notifications.length > 0 && (
          <button
            onClick={markAllAsRead}
            className="text-xs text-green-600 dark:text-green-400 hover:text-green-700 font-semibold transition-colors"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Scrollable list */}
      <div style={{ maxHeight: DROPDOWN_MAX_HEIGHT - 48 }} className="overflow-y-auto">
        {notifications.length === 0 ? (
          <div className="px-4 py-8 text-center text-gray-400 text-sm">
            No notifications
          </div>
        ) : (
          notifications.map((notification) => (
            <div
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              className={`px-4 py-3 border-b border-gray-100 dark:border-white/5 cursor-pointer hover:bg-gray-50 dark:hover:bg-white/5 transition-colors ${
                !notification.read ? 'bg-green-50 dark:bg-green-900/10' : ''
              }`}
            >
              <div className="flex items-start gap-3">
                <div className={`shrink-0 mt-0.5 ${typeColors[notification.type] || 'text-gray-400'}`}>
                  {typeIcons[notification.type] || typeIcons.general}
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-semibold ${!notification.read ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>
                    {notification.title}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                    {notification.message}
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    {timeAgo(notification.created_at)}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {!notification.read && (
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-1" />
                  )}
                  <button
                    onClick={(e) => handleDismiss(e, notification)}
                    className="text-gray-300 dark:text-white/20 hover:text-gray-500 dark:hover:text-white/50 transition-colors text-lg leading-none mt-0.5"
                    title="Dismiss"
                  >
                    ×
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>,
    document.body
  )

  return (
    <div ref={bellRef}>
      <button
        onClick={handleOpen}
        className="relative p-2 text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 w-4 h-4 bg-green-500 text-white text-xs font-black rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>
      {dropdown}
    </div>
  )
}